/*
 * hvac-pair-add-to-cart.js
 * Handles the "Kúpiť iba klimatizáciu" CTA on product pages:
 * adds the indoor variant + its paired outdoor variant to /cart/add.js as
 * two line items sharing a `_pair_id` property so the cart can group them.
 *
 * Voltage mode: the button carries `data-outdoor-handles` (CSV) and the
 * adjacent radio group lets the buyer pick 1-phase / 3-phase / etc.
 */
(function () {
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'pair-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  async function fetchProduct(handle) {
    const res = await fetch('/products/' + handle + '.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Product fetch failed: ' + handle);
    return res.json();
  }

  function selectedOutdoorHandle(btn) {
    const single = btn.dataset.outdoorHandle;
    if (single) return single;
    const csv = btn.dataset.outdoorHandles || '';
    const radios = document.querySelectorAll(
      'input[name="cta-voltage-' + btn.closest('[id^="cta-"]').id.replace('cta-', '') + '"]'
    );
    for (const r of radios) if (r.checked) return r.value;
    return csv.split(',')[0];
  }

  async function handleClick(e) {
    // Cart is not in use yet — the button is intentionally inert (no add-to-cart,
    // no /cart redirect). Delete the next two lines to restore the pair add-to-cart flow.
    e.preventDefault();
    return;
    const btn = e.currentTarget;
    if (btn.dataset.busy === '1') return;
    btn.dataset.busy = '1';
    const originalLabel = btn.querySelector('.cta__btn-label');
    const originalText = originalLabel ? originalLabel.textContent : '';
    if (originalLabel) originalLabel.textContent = 'Pridávam do košíka…';

    try {
      const indoorVariantId = btn.dataset.indoorVariantId;
      const outdoorHandle = selectedOutdoorHandle(btn);
      if (!indoorVariantId || !outdoorHandle) {
        throw new Error('Missing indoor or outdoor handle');
      }

      const outdoor = await fetchProduct(outdoorHandle);
      const outdoorVariant = outdoor.variants && outdoor.variants[0];
      if (!outdoorVariant) throw new Error('Outdoor product has no variants');

      const pairId = uuid();
      const supervisor = btn.dataset.needsSupervisor === 'true';
      const baseProps = { _pair_id: pairId };
      if (supervisor) baseProps._needs_supervisor = 'true';

      const body = {
        items: [
          {
            id: Number(indoorVariantId),
            quantity: 1,
            properties: Object.assign({}, baseProps, { _pair_role: 'indoor' })
          },
          {
            id: outdoorVariant.id,
            quantity: 1,
            properties: Object.assign({}, baseProps, { _pair_role: 'outdoor' })
          }
        ]
      };

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      if (!addRes.ok) {
        const errText = await addRes.text().catch(() => '');
        throw new Error('cart/add.js failed: ' + addRes.status + ' ' + errText);
      }
      window.location.href = '/cart';
    } catch (err) {
      console.error('[hvac-pair-add-to-cart]', err);
      if (originalLabel) originalLabel.textContent = originalText || 'Skúsiť znovu';
      btn.dataset.busy = '';
      alert('Nepodarilo sa pridať produkty do košíka. Skúste to prosím znovu alebo nás kontaktujte.');
    }
  }

  /*
   * AJAX variant swap — Brief Karta-ToDo #1.
   *
   * Click on a .rs__btn that points at a sibling product (different kW) →
   * fetch the product-dependent sections and swap them in place, pushState
   * the URL. No full page reload.
   *
   * Sections swapped:
   *   • main-product        — title, price, gallery, kW selector, buy buttons
   *   • product-info-columns — comparison table + technical parameters
   *     (the "Porovnajte si s podobnými modelmi / Technické parametre" block).
   *     These read product.metafields.hvac.*, so they MUST be re-rendered for
   *     the chosen kW; otherwise they keep showing the previous product's data.
   *
   * Uses Shopify's ?sections={id,id} query param, which returns a JSON map of
   * { sectionId: renderedHTML } rendered in the context of the requested
   * product. Section ids are identical across siblings (they belong to the
   * shared template), so the same ids resolve on the target product.
   *
   * If the fetch fails (404, network error, etc.), the caller falls back to a
   * hard navigation so the buyer always lands on the right page.
   */

  // Selectors for the extra, product-dependent sections (besides main-product)
  // that must be re-rendered when the kW / product changes.
  var DEPENDENT_SECTION_SELECTORS = ['.shopify-section.section-product-info-columns'];

  // Tepelné čerpadlá: aj tieto sekcie nesú údaje konkrétneho produktu (výkon, zásobník,
  // ovládanie, dotácia), takže sa pri prepnutí musia vymeniť. <product-info> čerpadla nesie
  // atribút data-cerpadlo; klimatizácia ho nemá a správa sa ako doteraz. FAQ nemá v schéme
  // vlastnú triedu, preto sa hľadá podľa konca id.
  var CERPADLO_SECTION_SELECTORS = [
    '.shopify-section.section-service-bar',
    '.shopify-section.section-product-features',
    '.shopify-section[id$="__faq"]',
    '.shopify-section.recommendations-section'
  ];

  function getMainSectionId() {
    var info = document.querySelector('product-info[data-section-id]');
    return info ? info.dataset.sectionId : null;
  }

  // Collect the on-page section ids (without the "shopify-section-" prefix) that
  // need swapping, plus a reference to each current element for the swap.
  function collectDependentSections(isCerpadlo) {
    var list = [];
    var selectors = isCerpadlo
      ? DEPENDENT_SECTION_SELECTORS.concat(CERPADLO_SECTION_SELECTORS)
      : DEPENDENT_SECTION_SELECTORS;
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (!el.id || el.id.indexOf('shopify-section-') !== 0) return;
        list.push({ el: el, id: el.id.replace('shopify-section-', '') });
      });
    });
    return list;
  }

  // innerHTML-inserted <script> tags don't execute; recreate them so they run.
  function reExecuteScripts(root) {
    root.querySelectorAll('script').forEach(function (oldScript) {
      // Skip external libraries (already loaded globally — re-injecting would
      // re-run customElements.define and throw) and non-JS data blocks.
      if (oldScript.src) return;
      var type = (oldScript.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module' && type !== 'application/javascript') return;
      var newScript = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        newScript.setAttribute(oldScript.attributes[i].name, oldScript.attributes[i].value);
      }
      newScript.text = oldScript.text;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  // Cesta, ktorej obsah je práve vykreslený na stránke. Po tlačidle Späť/Vpred je
  // window.location.pathname už cieľová cesta, takže podľa nej sa nedá poznať, či treba
  // obsah vymeniť — preto sa pamätá zvlášť.
  var renderedPath = window.location.pathname;

  // Poradie výmen. Pri 2–3 rýchlych ťukoch za sebou sa výmeny prekrývajú a každá si pred čakaním
  // zapamätá pôvodný <product-info>; druhá by potom volala replaceWith na už odpojený uzol a
  // stránka by ostala zmesou dvoch produktov (adresa, galéria a lišta z jedného, cena z druhého).
  // Platí len najnovšia výmena, staršie sa po načítaní zahodia skôr, než sa dotknú DOM.
  var swapSeq = 0;

  async function ajaxSwap(href, fromPop) {
    var mainSectionId = getMainSectionId();
    if (!mainSectionId) return false;
    var url;
    try { url = new URL(href, window.location.origin); } catch (e) { return false; }
    var pathMatch = url.pathname.match(/^\/products\/([^/?#]+)/);
    if (!pathMatch) return false;
    var mySeq = ++swapSeq;
    if (url.pathname === renderedPath) {
      // Already here; nothing to fetch
      return true;
    }

    var oldInfo = document.querySelector('product-info');
    if (!oldInfo) return false;
    var isCerpadlo = oldInfo.hasAttribute('data-cerpadlo');

    // Build the section list: main-product + every dependent section on the page.
    var dependents = collectDependentSections(isCerpadlo);
    var ids = [mainSectionId];
    dependents.forEach(function (d) { if (ids.indexOf(d.id) === -1) ids.push(d.id); });

    // Fetch each section via the singular ?section_id= endpoint (returns raw
    // section HTML). The plural ?sections= JSON endpoint is unreliable behind
    // the `shopify theme dev` HotReload proxy (it returns a `{product:…}` blob
    // instead of the section map), so we request one section at a time and run
    // the requests in parallel.
    var fetched;
    try {
      fetched = await Promise.all(ids.map(function (sid) {
        return fetch(url.pathname + '?section_id=' + encodeURIComponent(sid), {
          credentials: 'same-origin', headers: { 'Accept': 'text/html' }
        }).then(function (r) { return r.ok ? r.text() : null; }).catch(function () { return null; });
      }));
    } catch (e) { return false; }
    if (mySeq !== swapSeq) return true;   // medzitým sa spustila novšia výmena

    var parser = new DOMParser();
    var htmlById = {};
    ids.forEach(function (sid, i) { htmlById[sid] = fetched[i]; });

    // ── 1. Swap main-product ───────────────────────────────────────────────
    var mainHtml = htmlById[mainSectionId];
    if (!mainHtml) return false;
    var mainDoc = parser.parseFromString(mainHtml, 'text/html');

    // Čerpadlo: keď sa niektorá závislá sekcia nenačítala (alebo cieľ nie je čerpadlo), radšej
    // tvrdá navigácia než zmes údajov dvoch produktov. DOM sa zatiaľ nezmenil.
    if (isCerpadlo) {
      var missing = ids.some(function (sid) { return !htmlById[sid]; });
      var targetInfo = mainDoc.querySelector('product-info');
      if (missing || !targetInfo || !targetInfo.hasAttribute('data-cerpadlo')) return false;
    }

    // 1a. Media gallery — a sibling of <product-info>, NOT inside it. Swapping
    // it is what makes the photos change for colour switches (each colour is a
    // different product with its own media). For kW switches the media is the
    // same, so the browser just re-renders cached images.
    var newGallery = mainDoc.querySelector('.product__gallery-container');
    if (newGallery) {
      var oldGallery = document.querySelector('.product__gallery-container');
      if (oldGallery) {
        oldGallery.replaceWith(newGallery);
        reExecuteScripts(newGallery);
      }
    }

    // 1b. <product-info> (title, price, selectors, buy buttons).
    var newInfo = mainDoc.querySelector('product-info');
    if (!newInfo) return false;
    oldInfo.replaceWith(newInfo);
    reExecuteScripts(newInfo);

    // 1c. Spodná lepkavá lišta (.hsb) leží v main-product mimo <product-info>; bez výmeny
    // ostane s cenou a výkonom pôvodného čerpadla. Tlačidlá v nej sa obsluhujú delegovane.
    if (isCerpadlo) {
      var newBar = mainDoc.querySelector('[data-hvac-sticky-bar]');
      var oldBar = document.querySelector('[data-hvac-sticky-bar]');
      if (newBar && oldBar) oldBar.replaceWith(newBar);
    }

    // ── 2. Swap each dependent section (comparison / tech parameters) ───────
    dependents.forEach(function (d) {
      var html = htmlById[d.id];
      if (!html) return;
      var depDoc = parser.parseFromString(html, 'text/html');
      var newSection = depDoc.getElementById('shopify-section-' + d.id) || depDoc.body.firstElementChild;
      if (!newSection) return;
      // Re-resolve the current element in case the DOM moved; fall back to the
      // captured reference.
      var current = document.getElementById('shopify-section-' + d.id) || d.el;
      if (!current) return;
      current.replaceWith(newSection);
      reExecuteScripts(newSection);
    });

    // ── 3. URL + document title ────────────────────────────────────────────
    // Pri tlačidle Späť/Vpred je adresa už správna — história sa nesmie dopĺňať.
    if (!fromPop) history.pushState({}, '', url.pathname + url.search);
    renderedPath = url.pathname;
    // Čerpadlo má H1 všeobecný, takže titulok karty prehliadača nesie data-page-title.
    var newPageTitle = isCerpadlo ? newInfo.getAttribute('data-page-title') : '';
    if (newPageTitle) {
      document.title = newPageTitle;
    } else {
      var newH1 = newInfo.querySelector('h1');
      if (newH1) document.title = newH1.textContent.trim() + ' – CLIMA STUDIO';
    }

    // Re-init swatch nav, pair button, energy listener for the new content
    init();
    // Čerpadlo: výmena galérie zruší pozorovanie veľkosti (ResizeObserver v product-cta-dopyt.liquid
    // sleduje pôvodný, už odpojený uzol), takže po 2. prepnutí by lepkavý stĺpec s tromi CTA formulármi
    // ostal bez prepočtu (inset-block-start 160 px) a tlačidlá by sa schovali za spodnú lištu.
    // Udalosť resize prepočet spustí znova.
    if (isCerpadlo) window.dispatchEvent(new Event('resize'));
    document.dispatchEvent(new CustomEvent('hvac:section-swapped'));
    return true;
  }

  function initSwatchNav() {
    /*
     * Click handler for the sibling-product links:
     *   .rs__btn[data-handle]   — kW (capacity) buttons; same colour/media.
     *   .csw__item[data-handle] — colour/panel swatches; different media.
     *   .csw-panel__item[data-handle] — zásobník / zóny čerpadla.
     *   .nap__btn[data-handle]  — napájanie čerpadla (1-fázové / 3-fázové dvojča tej istej
     *                             sady, snippet cerpadlo-napajanie). Neaktívna dlaždica nie je
     *                             odkaz, takže sa sem nedostane.
     * All point at a sibling product. ajaxSwap re-renders the whole
     * main-product section (gallery + product-info) plus the comparison
     * section, so the photos change on colour switches too — no full reload.
     * Try AJAX swap first; fall back to hard navigation on any failure or
     * modifier keys (open-in-tab, etc).
     */
    document.querySelectorAll('.rs__btn[href][data-handle], .csw__item[href][data-handle], .csw-panel__item[href][data-handle], .nap__btn[href][data-handle]').forEach(function (el) {
      if (el.dataset.navBound === '1') return;
      el.dataset.navBound = '1';
      el.addEventListener('click', async function (e) {
        var href = el.getAttribute('href');
        if (!href || href === '#') return;
        // Allow modifier-key + middle-click default behaviour to pass through.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
        e.preventDefault();
        var swapped = await ajaxSwap(href);
        if (!swapped) {
          window.location.href = href;
        }
      });
    });
  }

  // Browser back/forward should also AJAX-swap so the URL and content match
  window.addEventListener('popstate', function () {
    // Len zmena kotvy (#…) alebo adresa mimo karty produktu — obsah ostáva.
    if (window.location.pathname === renderedPath) return;
    if (!/^\/products\/[^/?#]+/.test(window.location.pathname)) return;
    ajaxSwap(window.location.pathname + window.location.search, true)
      .then(function (ok) { if (!ok) window.location.reload(); })
      .catch(function () { window.location.reload(); });
  });

  /*
   * Room-size slider → product swap.
   *
   * Dragging the "veľkosť miestnosti" slider live-highlights the matching kW
   * container (handled in the slider's own script via syncSelector). That only
   * moved the highlight; the product never changed, so the comparison /
   * technical-parameters / colour-swatch sections stayed on the old data.
   *
   * Here we listen for the slider's `change` event (fires once, on release) and
   * run the SAME in-place swap as clicking the kW container — switching to the
   * highlighted kW's product so every dependent section updates. We use a
   * delegated listener so it keeps working for sliders re-rendered by a swap.
   *
   * The slider position is preserved across the swap: the freshly rendered
   * slider would otherwise snap back to its default value.
   */
  var sliderSwapBusy = false;

  function restoreSliderArea(area) {
    var input = document.querySelector('.rsl__input');
    if (!input || area == null) return;
    input.value = area;
    // Re-render bubble/recommendation for the new slider (no `change`, so this
    // does not re-trigger a swap).
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.classList || !input.classList.contains('rsl__input')) return;
    if (sliderSwapBusy) return;

    var activeBtn = document.querySelector('.rs__btn--active[href][data-handle]');
    if (!activeBtn) return;
    var href = activeBtn.getAttribute('href');
    if (!href || href === '#') return;

    // Already on the highlighted product → just leave the slider where it is.
    try {
      if (new URL(href, window.location.origin).pathname === renderedPath) return;
    } catch (err) { return; }

    var area = input.value;
    sliderSwapBusy = true;
    ajaxSwap(href)
      .then(function (ok) { if (ok) restoreSliderArea(area); })
      .catch(function () {})
      .finally(function () { sliderSwapBusy = false; });
  });

  /*
   * Reactive energy widget — listens for the slider's hvac:kw-change event and
   * rewrites the ~kWh / ~€ string from the data-energy-map (pre-computed per
   * sibling in Liquid). Karta-ToDo #2 follow-up.
   */
  function initEnergyReactive() {
    var blocks = document.querySelectorAll('[data-energy-dynamic="true"][data-energy-map]');
    if (!blocks.length) return;
    function parseMap(raw) {
      var map = {};
      (raw || '').split('~~').forEach(function (pair) {
        var p = pair.split('|');
        if (p.length !== 3) return;
        var key = (p[0] || '').replace(/\s+/g, '').toLowerCase().replace(',', '.');
        map[key] = { kwh: p[1], eur: p[2] };
      });
      return map;
    }
    blocks.forEach(function (block) {
      if (block.dataset.energyBound === '1') return;
      block.dataset.energyBound = '1';
      var map = parseMap(block.dataset.energyMap);
      var main = block.querySelector('.ecb__main');
      if (!main) return;
      // Prípona sa preberie z textu od servera, kým ho skript ešte neprepísal: čerpadlo má
      // „… € s DPH ročne", klimatizácia „… € s DPH /ročne".
      var origNode = main.childNodes[0];
      var origText = origNode && origNode.nodeType === 3 ? origNode.nodeValue : '';
      var suffix = origText.indexOf('/ročne') > -1 ? ' € s DPH /ročne ' : ' € s DPH ročne ';
      document.addEventListener('hvac:kw-change', function (e) {
        var k = (e.detail && e.detail.kw) || '';
        var key = k.replace(/\s+/g, '').toLowerCase().replace(',', '.');
        var entry = map[key];
        if (!entry || entry.kwh === '?' || entry.eur === '?') return;
        // Preserve the trailing tooltip span if present
        var tooltipSpan = main.querySelector('.ecb__info');
        var newText = '~' + entry.kwh + ' kWh / ~' + entry.eur + suffix;
        // Rewrite by recreating the text node before the tooltip
        var firstNode = main.childNodes[0];
        if (firstNode && firstNode.nodeType === 3) {
          firstNode.nodeValue = newText;
        } else {
          main.insertBefore(document.createTextNode(newText), main.firstChild);
        }
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-pair-add]').forEach(function (btn) {
      if (btn.dataset.pairBound === '1') return;
      btn.dataset.pairBound = '1';
      btn.addEventListener('click', handleClick);
    });
    initSwatchNav();
    initEnergyReactive();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
